package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Expense_entry
import com.tedd.db.Expense_list
import com.tedd.db.Expense_list_item
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

class ExpenseListRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Expense_list>> =
        db.expenseListQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeAllItems(): Flow<List<Expense_list_item>> =
        db.expenseListItemQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeItems(listId: String): Flow<List<Expense_list_item>> =
        db.expenseListItemQueries.selectByList(listId).asFlow().mapToList(Dispatchers.Default)

    suspend fun createList(
        name: String,
        description: String? = null,
        status: String = "active",
        date: String? = null,
        orderIndex: Long = 0,
        id: String = newId("el"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        // Nouvelle liste : ponctuelle par défaut (recurrence = 'once', mode 'reset').
        db.expenseListQueries.insert(id, name, description, status, date, "once", null, null, "reset", orderIndex, now, now)
        id
    }

    /**
     * Duplique une liste et ses éléments non supprimés. Les cases sont décochées et les
     * montants réels effacés : on repart d'une liste à faire, pas d'une copie déjà faite.
     */
    suspend fun cloneList(sourceId: String, newName: String): String? = withContext(Dispatchers.Default) {
        val source = db.expenseListQueries.selectAll().executeAsList().firstOrNull { it.id == sourceId }
            ?: return@withContext null
        val newId = createList(
            name = newName,
            description = source.description,
            status = "active",
            date = source.date,
            orderIndex = source.order_index + 1,
        )
        db.expenseListItemQueries.selectByList(sourceId).executeAsList()
            .filter { it.is_deleted == 0L }
            .forEachIndexed { index, item ->
                createItem(
                    listId = newId, name = item.name, planned = item.planned, actual = null,
                    catId = item.cat_id, isDone = false, orderIndex = index.toLong(),
                )
            }
        newId
    }

    suspend fun updateList(
        id: String, name: String, description: String?, status: String, date: String?, orderIndex: Long,
    ) = withContext(Dispatchers.Default) {
        db.expenseListQueries.update(name, description, status, date, orderIndex, nowMillis(), id)
    }

    /**
     * Configure la récurrence d'une liste. [nextResetDate] = première échéance de
     * réinitialisation (null pour `once` = liste ponctuelle, jamais réinitialisée).
     */
    suspend fun setRecurrence(id: String, recurrence: String, resetMode: String, nextResetDate: String?) =
        withContext(Dispatchers.Default) {
            db.expenseListQueries.setRecurrence(recurrence, resetMode, nextResetDate, nowMillis(), id)
        }

    /** Réinitialise les items d'une liste (non-cochés, actual effacé). */
    suspend fun resetItems(listId: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            // Le portefeuille repart plein : ses sous-dépenses partent avec la période.
            db.expenseEntryQueries.softDeleteForList(now, now, listId)
            db.expenseListItemQueries.resetForList(now, listId)
        }
    }

    /**
     * Mode `recreate` : archive un instantané daté de la période écoulée (items copiés
     * avec leur état coché/réel). La liste vivante est ensuite réinitialisée par l'appelant.
     *
     * L'identifiant de l'instantané est tiré de la liste et de la période : chaque appareil
     * qui joue la même échéance produit la même ligne, que la synchronisation fusionne, au
     * lieu d'une copie par appareil. Un instantané déjà là — même supprimé — n'est pas
     * refait : supprimer une copie la fait disparaître pour de bon.
     *
     * Une période où rien n'a été coché ni dépensé n'est pas archivée : la copie n'aurait
     * rien à montrer. Rend l'identifiant de l'instantané, ou null s'il n'y en a pas eu.
     */
    suspend fun archiveSnapshot(source: com.tedd.db.Expense_list, periodLabel: String): String? =
        withContext(Dispatchers.Default) {
            val snapId = snapshotId(source.id, periodLabel)
            val items = db.expenseListItemQueries.selectByList(source.id).executeAsList()
            if (items.none { it.is_done == 1L || (it.actual_ ?: 0L) != 0L }) return@withContext null
            db.transactionWithResult {
                if (db.expenseListQueries.selectByIdAnyState(snapId).executeAsOneOrNull() != null) return@transactionWithResult null
                val now = nowMillis()
                db.expenseListQueries.insert(
                    snapId, "${source.name} · $periodLabel", source.description, "archived",
                    source.last_reset_date ?: source.date, "once", null, null, "reset",
                    source.order_index, now, now,
                )
                items.forEach { item ->
                    val copyId = "${snapId}_${item.id}"
                    db.expenseListItemQueries.insert(
                        copyId, snapId, item.name, item.planned, item.actual_, item.cat_id,
                        item.is_done, item.order_index, now, now,
                    )
                    // Les sous-dépenses de la période suivent leur ligne dans l'instantané,
                    // sans leur transaction : la copie est un souvenir, elle ne paie rien.
                    db.expenseEntryQueries.selectByItem(item.id).executeAsList().forEach { e ->
                        db.expenseEntryQueries.insert(
                            "${copyId}_${e.id}", copyId, e.label, e.amount, e.date, e.note, null, e.order_index, now, now,
                        )
                    }
                }
                snapId
            }
        }

    /** L'identifiant de l'instantané d'une liste pour une période : le même partout. */
    fun snapshotId(listId: String, periodLabel: String) = "${listId}_snap_$periodLabel"

    /** Enregistre une réinitialisation jouée et cale la prochaine échéance. */
    suspend fun advanceReset(id: String, lastResetDate: String, nextResetDate: String) =
        withContext(Dispatchers.Default) {
            db.expenseListQueries.advanceReset(lastResetDate, nextResetDate, nowMillis(), id)
        }

    suspend fun softDeleteList(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.expenseListQueries.softDelete(now, now, id)
    }

    suspend fun createItem(
        listId: String,
        name: String,
        planned: Long,
        actual: Long? = null,
        catId: String? = null,
        isDone: Boolean = false,
        orderIndex: Long = 0,
        id: String = newId("eli"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.expenseListItemQueries.insert(
            id, listId, name, planned, actual, catId, if (isDone) 1L else 0L, orderIndex, now, now,
        )
        id
    }

    suspend fun updateItem(
        id: String, name: String, planned: Long, actual: Long?, catId: String?, isDone: Boolean, orderIndex: Long,
    ) = withContext(Dispatchers.Default) {
        db.expenseListItemQueries.update(
            name, planned, actual, catId, if (isDone) 1L else 0L, orderIndex, nowMillis(), id,
        )
    }

    suspend fun toggleItemDone(id: String) = withContext(Dispatchers.Default) {
        db.expenseListItemQueries.toggleDone(nowMillis(), id)
    }

    // ── Sous-dépenses (portefeuille d'une ligne) ───────────────────────
    //
    // Une sous-dépense peut se payer tout de suite depuis un compte : elle porte alors
    // la transaction qui l'a débitée. Modifier ou retirer la sous-dépense suit la
    // transaction ; cocher la ligne ne débite plus que ce qui n'a pas encore été payé.

    fun observeAllEntries(): Flow<List<Expense_entry>> =
        db.expenseEntryQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    /** Ajoute une sous-dépense à la ligne [itemId] ; le réel de la ligne suit. */
    suspend fun addEntry(itemId: String, label: String, amount: Long, date: String?, note: String?, id: String = newId("ee")): String =
        withContext(Dispatchers.Default) {
            val now = nowMillis()
            db.transaction {
                db.expenseEntryQueries.insert(id, itemId, label.trim(), amount, date, note?.trim()?.ifBlank { null }, null, 0L, now, now)
                refreshActual(itemId, now)
            }
            id
        }

    /**
     * Ajoute une sous-dépense et la paie tout de suite : une transaction de dépense sur
     * [accountId], le solde débité, la sous-dépense qui s'en souvient, le réel de la ligne
     * à jour. Tout ou rien, dans une seule transaction SQL. Rend l'identifiant de la sous-dépense.
     */
    suspend fun addEntryPaid(
        item: Expense_list_item,
        label: String,
        amount: Long,
        date: String?,
        note: String?,
        accountId: String,
        id: String = newId("ee"),
        txId: String = newId("tx"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val day = date ?: org.teddapp.project.db.TODAY_ISO
        db.transaction {
            db.financeTransactionQueries.insert(
                txId, day, accountId, null, item.cat_id, "expense", amount, "${item.name} · ${label.trim()}", null, now, now,
            )
            FinanceTransactionRepository.applyEffectsNow(db, "expense", accountId, null, amount, +1, now)
            db.expenseEntryQueries.insert(id, item.id, label.trim(), amount, day, note?.trim()?.ifBlank { null }, txId, 0L, now, now)
            refreshActual(item.id, now)
        }
        id
    }

    /** Modifie une sous-dépense ; si elle a été payée, sa transaction suit (montant, date, libellé). */
    suspend fun updateEntry(entry: Expense_entry, label: String, amount: Long, date: String?, note: String?) =
        withContext(Dispatchers.Default) {
            val now = nowMillis()
            db.transaction {
                val tx = entry.tx_id?.let { db.financeTransactionQueries.selectByIdAnyState(it).executeAsOneOrNull() }
                    ?.takeIf { it.is_deleted == 0L }
                if (tx != null) {
                    FinanceTransactionRepository.applyEffectsNow(db, tx.type, tx.account_id, tx.to_account_id, tx.amount, -1, now)
                    db.financeTransactionQueries.update(
                        date ?: tx.date, tx.account_id, tx.to_account_id, tx.cat_id, tx.type, amount,
                        tx.description?.substringBefore(" · ")?.let { "$it · ${label.trim()}" } ?: label.trim(),
                        tx.fund_id, now, tx.id,
                    )
                    FinanceTransactionRepository.applyEffectsNow(db, tx.type, tx.account_id, tx.to_account_id, amount, +1, now)
                }
                db.expenseEntryQueries.update(label.trim(), amount, date, note?.trim()?.ifBlank { null }, now, entry.id)
                refreshActual(entry.item_id, now)
            }
        }

    /** Retire une sous-dépense ; si elle a été payée, la transaction s'efface et le solde revient. */
    suspend fun deleteEntry(entry: Expense_entry) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            val tx = entry.tx_id?.let { db.financeTransactionQueries.selectByIdAnyState(it).executeAsOneOrNull() }
                ?.takeIf { it.is_deleted == 0L }
            if (tx != null) {
                db.financeTransactionQueries.softDelete(now, now, tx.id)
                FinanceTransactionRepository.applyEffectsNow(db, tx.type, tx.account_id, tx.to_account_id, tx.amount, -1, now)
            }
            db.expenseEntryQueries.softDelete(now, now, entry.id)
            refreshActual(entry.item_id, now)
        }
    }

    /** Ce qui a déjà été payé sur la ligne par ses sous-dépenses. */
    suspend fun paidForItem(itemId: String): Long = withContext(Dispatchers.Default) {
        db.expenseEntryQueries.paidForItem(itemId).executeAsOne()
    }

    /**
     * Coche une ligne comme dépensée pour [actual] : une transaction sur [accountId] du
     * restant seulement, ce que les sous-dépenses ont déjà payé n'est pas débité deux fois.
     * Rend le montant effectivement débité, zéro si tout était déjà payé.
     */
    suspend fun markDone(item: Expense_list_item, actual: Long, accountId: String, date: String, description: String): Long =
        withContext(Dispatchers.Default) {
            val now = nowMillis()
            db.transactionWithResult {
                val due = (actual - db.expenseEntryQueries.paidForItem(item.id).executeAsOne()).coerceAtLeast(0L)
                if (due > 0L) {
                    db.financeTransactionQueries.insert(
                        newId("tx"), date, accountId, null, item.cat_id, "expense", due, description, null, now, now,
                    )
                    FinanceTransactionRepository.applyEffectsNow(db, "expense", accountId, null, due, +1, now)
                }
                db.expenseListItemQueries.update(item.name, item.planned, actual, item.cat_id, 1L, item.order_index, now, item.id)
                due
            }
        }

    /**
     * Le réel d'une ligne-portefeuille : la somme de ses sous-dépenses. Quand la dernière
     * est retirée, la ligne redevient une ligne ordinaire, sans réel.
     */
    private fun refreshActual(itemId: String, now: Long) {
        val t = db.expenseEntryQueries.totalForItem(itemId).executeAsOne()
        db.expenseListItemQueries.setActual(if (t.n > 0L) t.total else null, now, itemId)
    }

    suspend fun softDeleteItem(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.expenseListItemQueries.softDelete(now, now, id)
    }
}
